import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { FlightSegment, FlightPricing, TripData } from "./useTrips";
import type { Json } from "@/integrations/supabase/types";

// ─── Aviationstack (operational) enrichment ─────────────────────────────────

interface FlightEnrichment {
  aircraft?: string;
  flight_status?: string;
  departure_terminal?: string;
  departure_gate?: string;
  departure_timezone?: string;
  departure_scheduled?: string;
  departure_delay?: number;
  arrival_terminal?: string;
  arrival_gate?: string;
  arrival_timezone?: string;
  arrival_scheduled?: string;
  arrival_delay?: number;
  stops?: number;
  airline_name?: string;
}

// ─── Google Flights (price + details) enrichment ────────────────────────────

interface FlightDetailResult {
  flight_number: string;
  airplane?: string;
  duration_minutes?: number;
  airline_logo?: string;
  legroom?: string;
  overnight?: boolean;
  often_delayed?: boolean;
  operated_by?: string;
}

interface PriceEnrichmentResult {
  retail_price: number;
  currency: string;
  flight_details: FlightDetailResult[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Check if a flight needs operational enrichment (has empty enrichable fields).
 */
export function needsEnrichment(flight: FlightSegment, pricings?: FlightPricing[]): boolean {
  const needsOperational = !flight.enriched && (
    !flight.aircraft ||
    !flight.status ||
    !flight.departure_timezone ||
    !flight.arrival_timezone ||
    !flight.departure_terminal ||
    !flight.arrival_terminal ||
    flight.stops === undefined
  );

  const needsPrice = !pricings?.some(p =>
    p.flight_numbers.includes(flight.flight_number)
  );

  return needsOperational || needsPrice;
}

/**
 * Group flights by booking (confirmation_number).
 * Flights without a confirmation_number each get their own group.
 */
function groupFlightsByBooking(
  flights: FlightSegment[],
): Map<string, { flights: FlightSegment[]; indices: number[] }> {
  const groups = new Map<string, { flights: FlightSegment[]; indices: number[] }>();

  flights.forEach((flight, index) => {
    const key = flight.confirmation_number || `__solo_${index}_${flight.flight_number}`;
    const group = groups.get(key) || { flights: [], indices: [] };
    group.flights.push(flight);
    group.indices.push(index);
    groups.set(key, group);
  });

  return groups;
}

/**
 * Apply Aviationstack operational enrichment to a flight.
 */
function applyEnrichment(flight: FlightSegment, enrichment: FlightEnrichment): FlightSegment {
  const totalDelay = (enrichment.departure_delay || 0) + (enrichment.arrival_delay || 0);

  return {
    ...flight,
    aircraft: enrichment.aircraft || flight.aircraft,
    status: enrichment.flight_status || flight.status,
    departure_timezone: enrichment.departure_timezone || flight.departure_timezone,
    departure_terminal: enrichment.departure_terminal || flight.departure_terminal,
    departure_gate: enrichment.departure_gate || flight.departure_gate,
    departure_time: flight.departure_time || enrichment.departure_scheduled,
    arrival_timezone: enrichment.arrival_timezone || flight.arrival_timezone,
    arrival_terminal: enrichment.arrival_terminal || flight.arrival_terminal,
    arrival_gate: enrichment.arrival_gate || flight.arrival_gate,
    arrival_time: flight.arrival_time || enrichment.arrival_scheduled,
    stops: enrichment.stops ?? flight.stops,
    delay: totalDelay > 0 ? totalDelay : flight.delay,
    airline: enrichment.airline_name || flight.airline,
    enriched: true,
  };
}

/**
 * Apply Google Flights per-leg detail fields to matching FlightSegments.
 */
function applyFlightDetails(
  flights: FlightSegment[],
  details: FlightDetailResult[],
): FlightSegment[] {
  if (!details || details.length === 0) return flights;

  const detailMap = new Map(
    details.map(d => [d.flight_number.replace(/\s+/g, '').toUpperCase(), d])
  );

  return flights.map(flight => {
    const key = flight.flight_number.replace(/\s+/g, '').toUpperCase();
    const detail = detailMap.get(key);
    if (!detail) return flight;

    return {
      ...flight,
      aircraft: flight.aircraft || detail.airplane,
      duration_minutes: detail.duration_minutes ?? flight.duration_minutes,
      airline_logo: detail.airline_logo || flight.airline_logo,
      legroom: detail.legroom || flight.legroom,
      overnight: detail.overnight ?? flight.overnight,
      often_delayed: detail.often_delayed ?? flight.often_delayed,
      operated_by: detail.operated_by || flight.operated_by,
    };
  });
}

/**
 * Apply retail price from enrichment to matching FlightSegments' pricing field.
 */
function applyRetailPrice(
  flights: FlightSegment[],
  flightNumbers: string[],
  priceResult: PriceEnrichmentResult,
): FlightSegment[] {
  const normalizedNumbers = new Set(
    flightNumbers.map(fn => fn.replace(/\s+/g, '').toUpperCase())
  );

  return flights.map(flight => {
    const key = flight.flight_number.replace(/\s+/g, '').toUpperCase();
    if (!normalizedNumbers.has(key)) return flight;

    return {
      ...flight,
      pricing: {
        ...(flight.pricing || {}),
        retail_price: priceResult.retail_price,
        retail_currency: priceResult.currency,
        retail_fetched_at: new Date().toISOString(),
      },
    };
  });
}

/**
 * Upsert a FlightPricing entry into the pricings array.
 * If an entry already exists for the same flight_numbers set, replace it.
 */
function upsertPricing(
  pricings: FlightPricing[],
  flightNumbers: string[],
  priceResult: PriceEnrichmentResult,
  confirmationNumber?: string,
): FlightPricing[] {
  const sortedNumbers = [...flightNumbers].sort();
  const updated = pricings.filter(p => {
    const existing = [...p.flight_numbers].sort();
    return JSON.stringify(existing) !== JSON.stringify(sortedNumbers);
  });

  updated.push({
    retail_price: priceResult.retail_price,
    currency: priceResult.currency,
    flight_numbers: flightNumbers,
    confirmation_number: confirmationNumber,
    fetched_at: new Date().toISOString(),
  });

  return updated;
}

// ─── API fetchers ───────────────────────────────────────────────────────────

/**
 * Fetch Aviationstack operational enrichment for a single flight.
 */
async function fetchFlightEnrichment(flight: FlightSegment): Promise<FlightEnrichment | null> {
  try {
    const { data, error } = await supabase.functions.invoke('enrich-flight', {
      body: {
        flight_number: flight.flight_number,
        flight_date: flight.departure_time || flight.departure_date,
      },
    });

    if (error) {
      console.error('Error calling enrich-flight:', error);
      return null;
    }

    return data?.enrichment as FlightEnrichment | null;
  } catch (err) {
    console.error('Exception calling enrich-flight:', err);
    return null;
  }
}

/**
 * Fetch Google Flights price enrichment for a booking group of flights.
 */
async function fetchFlightPriceEnrichment(
  flights: FlightSegment[],
): Promise<PriceEnrichmentResult | null> {
  try {
    const flightInputs = flights.map(f => ({
      departure_airport: f.departure_airport,
      arrival_airport: f.arrival_airport,
      departure_date: f.departure_date || f.departure_time,
      flight_number: f.flight_number,
    }));

    const { data, error } = await supabase.functions.invoke('enrich-flight-price', {
      body: { flights: flightInputs },
    });

    if (error) {
      console.error('Error calling enrich-flight-price:', error);
      return null;
    }

    return data?.enrichment as PriceEnrichmentResult | null;
  } catch (err) {
    console.error('Exception calling enrich-flight-price:', err);
    return null;
  }
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Manual enrichment for a single flight.
 * Runs Aviationstack (operational) and Google Flights (price) in parallel.
 */
export function useEnrichFlight() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tripId,
      flightIndex,
      flight,
      allFlights,
    }: {
      tripId: string;
      flightIndex: number;
      flight: FlightSegment;
      allFlights?: FlightSegment[];
    }) => {
      // Determine the booking group for price enrichment
      const bookingFlights = allFlights
        ? allFlights.filter(f =>
            flight.confirmation_number &&
            f.confirmation_number === flight.confirmation_number
          )
        : [flight];

      // If no confirmation_number match found, just use the single flight
      const priceGroup = bookingFlights.length > 0 ? bookingFlights : [flight];

      // Run both enrichments in parallel
      const [opResult, priceResult] = await Promise.allSettled([
        fetchFlightEnrichment(flight),
        fetchFlightPriceEnrichment(priceGroup),
      ]);

      const enrichment = opResult.status === 'fulfilled' ? opResult.value : null;
      const priceEnrichment = priceResult.status === 'fulfilled' ? priceResult.value : null;

      if (!enrichment && !priceEnrichment) {
        return { enriched: false, message: 'No enrichment data found' };
      }

      // Fetch trip and update
      const { data: trip, error: fetchError } = await supabase
        .from("trips")
        .select("*")
        .eq("id", tripId)
        .single();

      if (fetchError) throw fetchError;

      const tripData = (trip.trip_data as TripData) || { flights: [], flight_pricings: [], hotels: [], cars: [], activities: [], cruises: [] };
      let flights = [...(tripData.flights || [])];
      let pricings = [...(tripData.flight_pricings || [])];

      // Apply operational enrichment
      if (enrichment) {
        flights[flightIndex] = applyEnrichment(flights[flightIndex], enrichment);
      }

      // Apply price enrichment + per-leg details
      if (priceEnrichment) {
        const flightNumbers = priceGroup.map(f => f.flight_number);
        pricings = upsertPricing(pricings, flightNumbers, priceEnrichment, flight.confirmation_number);
        flights = applyFlightDetails(flights, priceEnrichment.flight_details);
        flights = applyRetailPrice(flights, flightNumbers, priceEnrichment);
      }

      const updatedTripData = { ...tripData, flights, flight_pricings: pricings };

      const { error: updateError } = await supabase
        .from("trips")
        .update({ trip_data: updatedTripData as unknown as Json })
        .eq("id", tripId);

      if (updateError) throw updateError;

      return { enriched: true, enrichment, priceEnrichment };
    },
    onSuccess: (result, variables) => {
      if (result.enriched) {
        queryClient.invalidateQueries({ queryKey: ["trip", variables.tripId] });
        queryClient.invalidateQueries({ queryKey: ["trips"] });
        toast.success("Flight enriched with aircraft and status data");
      } else {
        toast.info(result.message || "No additional flight data available");
      }
    },
    onError: (error) => {
      console.error("Flight enrichment error:", error);
      toast.error("Failed to enrich flight data");
    },
  });
}

/**
 * Batch enrichment for all flights in a trip.
 * Runs Aviationstack per-flight and Google Flights per-booking-group, all in parallel.
 */
export function useEnrichAllFlights() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tripId,
      flights,
    }: {
      tripId: string;
      flights: FlightSegment[];
    }) => {
      // Operational enrichment: one call per flight
      const opPromises = flights.map(async (flight, index) => {
        const enrichment = await fetchFlightEnrichment(flight);
        return { index, enrichment };
      });

      // Price enrichment: one call per booking group
      const groups = groupFlightsByBooking(flights);
      const pricePromises = [...groups.entries()].map(async ([key, group]) => {
        const priceEnrichment = await fetchFlightPriceEnrichment(group.flights);
        return {
          key,
          flightNumbers: group.flights.map(f => f.flight_number),
          confirmationNumber: group.flights[0].confirmation_number,
          priceEnrichment,
        };
      });

      // Run everything in parallel
      const [opResults, priceResults] = await Promise.all([
        Promise.allSettled(opPromises),
        Promise.allSettled(pricePromises),
      ]);

      // Fetch current trip data
      const { data: trip, error: fetchError } = await supabase
        .from("trips")
        .select("*")
        .eq("id", tripId)
        .single();

      if (fetchError) throw fetchError;

      const tripData = (trip.trip_data as TripData) || { flights: [], flight_pricings: [], hotels: [], cars: [], activities: [], cruises: [] };
      const updatedFlights = [...(tripData.flights || [])];
      let pricings = [...(tripData.flight_pricings || [])];

      // Apply operational enrichments
      let opCount = 0;
      for (const result of opResults) {
        if (result.status === 'fulfilled' && result.value.enrichment) {
          const { index, enrichment } = result.value;
          updatedFlights[index] = applyEnrichment(updatedFlights[index], enrichment);
          opCount++;
        }
      }

      // Apply price enrichments + per-leg details + retail prices
      let priceCount = 0;
      for (const result of priceResults) {
        if (result.status === 'fulfilled' && result.value.priceEnrichment) {
          const { flightNumbers, confirmationNumber, priceEnrichment } = result.value;
          pricings = upsertPricing(pricings, flightNumbers, priceEnrichment, confirmationNumber);
          const detailed = applyFlightDetails(updatedFlights, priceEnrichment.flight_details);
          const priced = applyRetailPrice(detailed, flightNumbers, priceEnrichment);
          for (let i = 0; i < updatedFlights.length; i++) {
            updatedFlights[i] = priced[i];
          }
          priceCount++;
        }
      }

      if (opCount === 0 && priceCount === 0) {
        return { enrichedCount: 0 };
      }

      const updatedTripData = { ...tripData, flights: updatedFlights, flight_pricings: pricings };

      const { error: updateError } = await supabase
        .from("trips")
        .update({ trip_data: updatedTripData as unknown as Json })
        .eq("id", tripId);

      if (updateError) throw updateError;

      return { enrichedCount: opCount + priceCount };
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ["trip", variables.tripId] });
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      if (result.enrichedCount > 0) {
        toast.success(`Enriched ${result.enrichedCount} flight(s) with additional data`);
      } else {
        toast.info("No additional flight data available from API");
      }
    },
    onError: (error) => {
      console.error("Flight enrichment error:", error);
      toast.error("Failed to enrich flight data");
    },
  });
}

/**
 * Auto-enrich a newly added flight if it needs enrichment.
 * Runs both operational and price enrichment in parallel. Silent on failure.
 */
export function useAutoEnrichFlight() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tripId,
      flightIndex,
      flight,
    }: {
      tripId: string;
      flightIndex: number;
      flight: FlightSegment;
    }) => {
      // Run both enrichments in parallel
      const [opResult, priceResult] = await Promise.allSettled([
        fetchFlightEnrichment(flight),
        fetchFlightPriceEnrichment([flight]),
      ]);

      const enrichment = opResult.status === 'fulfilled' ? opResult.value : null;
      const priceEnrichment = priceResult.status === 'fulfilled' ? priceResult.value : null;

      if (!enrichment && !priceEnrichment) {
        return { enriched: false, skipped: false };
      }

      // Fetch and update trip data
      const { data: trip, error: fetchError } = await supabase
        .from("trips")
        .select("*")
        .eq("id", tripId)
        .single();

      if (fetchError) throw fetchError;

      const tripData = (trip.trip_data as TripData) || { flights: [], flight_pricings: [], hotels: [], cars: [], activities: [], cruises: [] };
      let flights = [...(tripData.flights || [])];
      let pricings = [...(tripData.flight_pricings || [])];

      if (flightIndex >= flights.length) {
        return { enriched: false, skipped: true };
      }

      if (enrichment) {
        flights[flightIndex] = applyEnrichment(flights[flightIndex], enrichment);
      }

      if (priceEnrichment) {
        const flightNumbers = [flight.flight_number];
        pricings = upsertPricing(
          pricings,
          flightNumbers,
          priceEnrichment,
          flight.confirmation_number,
        );
        flights = applyFlightDetails(flights, priceEnrichment.flight_details);
        flights = applyRetailPrice(flights, flightNumbers, priceEnrichment);
      }

      const updatedTripData = { ...tripData, flights, flight_pricings: pricings };

      const { error: updateError } = await supabase
        .from("trips")
        .update({ trip_data: updatedTripData as unknown as Json })
        .eq("id", tripId);

      if (updateError) throw updateError;

      return { enriched: true, enrichment, priceEnrichment };
    },
    onSuccess: (result, variables) => {
      if (result.enriched) {
        queryClient.invalidateQueries({ queryKey: ["trip", variables.tripId] });
        queryClient.invalidateQueries({ queryKey: ["trips"] });
        toast.success("Flight data enriched automatically");
      }
    },
    onError: (error) => {
      // Silent failure for auto-enrich
      console.error("Auto-enrich error:", error);
    },
  });
}
